import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Alert, Badge, Table, ProgressBar, Accordion, Form, Button } from 'react-bootstrap';
import { FaBrain, FaLightbulb, FaExclamationTriangle, FaCheckCircle, FaArrowUp, FaArrowDown, FaSearch, FaHome } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import transactionService from '../services/transactionService';
import categoryService from '../services/categoryService';
import { CATEGORY_TYPES } from '../constants/categoryTypes';

const FinancialInsights = () => {
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [insights, setInsights] = useState({});
  const [selectedTimeframe, setSelectedTimeframe] = useState('6months');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (transactions.length > 0) {
      generateInsights();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactions, categories, selectedTimeframe]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [transactionsData, categoriesData] = await Promise.all([
        transactionService.getAll(),
        categoryService.getAll()
      ]);

      setTransactions(transactionsData);
      setCategories(categoriesData);
    } catch (err) {
      setError('Erro ao carregar dados: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const generateInsights = () => {
    const filteredTransactions = getFilteredTransactions();
    
    const insights = {
      spendingPatterns: analyzeSpendingPatterns(filteredTransactions),
      budgetHealth: analyzeBudgetHealth(filteredTransactions),
      savingsOpportunities: findSavingsOpportunities(filteredTransactions),
      seasonalTrends: analyzeSeasonalTrends(filteredTransactions),
      categoryInsights: analyzeCategoryInsights(filteredTransactions),
      riskAlerts: generateRiskAlerts(filteredTransactions),
      recommendations: generateRecommendations(filteredTransactions)
    };

    setInsights(insights);
  };

  const getFilteredTransactions = () => {
    const now = new Date();
    let startDate;

    switch (selectedTimeframe) {
      case '3months':
        startDate = new Date(now.getFullYear(), now.getMonth() - 3, 1);
        break;
      case '6months':
        startDate = new Date(now.getFullYear(), now.getMonth() - 6, 1);
        break;
      case '12months':
        startDate = new Date(now.getFullYear() - 1, now.getMonth(), 1);
        break;
      default:
        startDate = new Date(now.getFullYear(), now.getMonth() - 6, 1);
    }

    return transactions.filter(t => new Date(t.date) >= startDate);
  };

  const analyzeSpendingPatterns = (filteredTransactions) => {
    const monthlySpending = {};
    const categorySpending = {};
    const weekdaySpending = Array(7).fill(0);
    let unusualTransactions = [];

    filteredTransactions.forEach(transaction => {
      const date = new Date(transaction.date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const categoryId = transaction.categoryId;
      const weekday = date.getDay();

      // Gastos mensais
      if (!monthlySpending[monthKey]) {
        monthlySpending[monthKey] = { income: 0, expense: 0 };
      }
      if (transaction.amount > 0) {
        monthlySpending[monthKey].income += transaction.amount;
      } else {
        monthlySpending[monthKey].expense += Math.abs(transaction.amount);
      }

      // Gastos por categoria
      if (!categorySpending[categoryId]) {
        categorySpending[categoryId] = 0;
      }
      if (transaction.amount < 0) {
        categorySpending[categoryId] += Math.abs(transaction.amount);
      }

      // Gastos por dia da semana
      if (transaction.amount < 0) {
        weekdaySpending[weekday] += Math.abs(transaction.amount);
      }

      // Detectar transações incomuns (valor muito alto)
      const category = categories.find(c => c.id === categoryId);
      const avgForCategory = categorySpending[categoryId] / filteredTransactions.filter(t => t.categoryId === categoryId).length;
      if (Math.abs(transaction.amount) > avgForCategory * 3) {
        unusualTransactions.push({
          ...transaction,
          categoryName: category?.name || 'N/A',
          multiplier: Math.abs(transaction.amount) / avgForCategory
        });
      }
    });

    // Calcular médias e tendências
    const monthlyValues = Object.values(monthlySpending);
    const avgMonthlyIncome = monthlyValues.reduce((sum, m) => sum + m.income, 0) / monthlyValues.length;
    const avgMonthlyExpense = monthlyValues.reduce((sum, m) => sum + m.expense, 0) / monthlyValues.length;

    // Detectar tendência de gastos
    const recentMonths = monthlyValues.slice(-3);
    const earlierMonths = monthlyValues.slice(0, 3);
    const recentAvgExpense = recentMonths.reduce((sum, m) => sum + m.expense, 0) / recentMonths.length;
    const earlierAvgExpense = earlierMonths.reduce((sum, m) => sum + m.expense, 0) / earlierMonths.length;
    const spendingTrend = recentAvgExpense > earlierAvgExpense ? 'increasing' : 'decreasing';

    return {
      avgMonthlyIncome,
      avgMonthlyExpense,
      spendingTrend,
      weekdaySpending,
      unusualTransactions: unusualTransactions.slice(0, 5),
      monthlySpending
    };
  };

  const analyzeBudgetHealth = (filteredTransactions) => {
    const totalIncome = filteredTransactions
      .filter(t => t.amount > 0)
      .reduce((sum, t) => sum + t.amount, 0);
    
    const totalExpense = Math.abs(filteredTransactions
      .filter(t => t.amount < 0)
      .reduce((sum, t) => sum + t.amount, 0));

    const netResult = totalIncome - totalExpense;
    const savingsRate = totalIncome > 0 ? (netResult / totalIncome) * 100 : 0;

    // Classificar saúde financeira
    let healthScore = 0;
    let healthLevel = 'critical';
    let healthColor = 'danger';

    if (savingsRate >= 20) {
      healthScore = 100;
      healthLevel = 'excellent';
      healthColor = 'success';
    } else if (savingsRate >= 10) {
      healthScore = 80;
      healthLevel = 'good';
      healthColor = 'success';
    } else if (savingsRate >= 5) {
      healthScore = 60;
      healthLevel = 'fair';
      healthColor = 'warning';
    } else if (savingsRate >= 0) {
      healthScore = 40;
      healthLevel = 'poor';
      healthColor = 'warning';
    } else {
      healthScore = 20;
      healthLevel = 'critical';
      healthColor = 'danger';
    }

    return {
      totalIncome,
      totalExpense,
      netResult,
      savingsRate,
      healthScore,
      healthLevel,
      healthColor
    };
  };

  const findSavingsOpportunities = (filteredTransactions) => {
    const categoryTotals = {};
    
    filteredTransactions.forEach(transaction => {
      if (transaction.amount < 0) {
        const categoryId = transaction.categoryId;
        if (!categoryTotals[categoryId]) {
          const category = categories.find(c => c.id === categoryId);
          categoryTotals[categoryId] = {
            id: categoryId,
            name: category?.name || 'N/A',
            type: category?.type || 'UNKNOWN',
            total: 0,
            count: 0,
            avg: 0
          };
        }
        categoryTotals[categoryId].total += Math.abs(transaction.amount);
        categoryTotals[categoryId].count++;
      }
    });

    // Calcular médias e encontrar oportunidades
    Object.values(categoryTotals).forEach(cat => {
      cat.avg = cat.total / cat.count;
    });

    // Top categorias por gasto
    const topExpenseCategories = Object.values(categoryTotals)
      .sort((a, b) => b.total - a.total)
      .slice(0, 5);

    // Categorias com transações frequentes (oportunidade de otimização)
    const frequentCategories = Object.values(categoryTotals)
      .filter(cat => cat.count > filteredTransactions.length * 0.05)
      .sort((a, b) => b.count - a.count)
      .slice(0, 3);

    return {
      topExpenseCategories,
      frequentCategories
    };
  };

  const analyzeSeasonalTrends = (filteredTransactions) => {
    const monthlyTrends = Array(12).fill().map((_, i) => ({
      month: i + 1,
      name: getMonthName(i + 1),
      income: 0,
      expense: 0,
      count: 0
    }));

    filteredTransactions.forEach(transaction => {
      const month = new Date(transaction.date).getMonth();
      if (transaction.amount > 0) {
        monthlyTrends[month].income += transaction.amount;
      } else {
        monthlyTrends[month].expense += Math.abs(transaction.amount);
      }
      monthlyTrends[month].count++;
    });

    // Encontrar mês com maior gasto e maior receita
    const highestExpenseMonth = monthlyTrends.reduce((max, current) => 
      current.expense > max.expense ? current : max
    );

    const highestIncomeMonth = monthlyTrends.reduce((max, current) => 
      current.income > max.income ? current : max
    );

    return {
      monthlyTrends,
      highestExpenseMonth,
      highestIncomeMonth
    };
  };

  const analyzeCategoryInsights = (filteredTransactions) => {
    const typeAnalysis = {};

    Object.entries(CATEGORY_TYPES).forEach(([key, config]) => {
      typeAnalysis[key] = {
        type: key,
        label: config.label,
        isExpense: config.isExpense,
        total: 0,
        percentage: 0,
        trend: 'stable'
      };
    });

    filteredTransactions.forEach(transaction => {
      const category = categories.find(c => c.id === transaction.categoryId);
      if (category && typeAnalysis[category.type]) {
        typeAnalysis[category.type].total += Math.abs(transaction.amount);
      }
    });

    // Calcular percentuais
    const totalExpenses = Object.values(typeAnalysis)
      .filter(t => t.isExpense)
      .reduce((sum, t) => sum + t.total, 0);

    Object.values(typeAnalysis).forEach(type => {
      if (type.isExpense && totalExpenses > 0) {
        type.percentage = (type.total / totalExpenses) * 100;
      }
    });

    return Object.values(typeAnalysis)
      .filter(t => t.total > 0)
      .sort((a, b) => b.total - a.total);
  };

  const generateRiskAlerts = (filteredTransactions) => {
    const alerts = [];
    const patterns = analyzeSpendingPatterns(filteredTransactions);
    const health = analyzeBudgetHealth(filteredTransactions);

    // Alert para taxa de poupança baixa
    if (health.savingsRate < 5) {
      alerts.push({
        type: 'danger',
        icon: FaExclamationTriangle,
        title: 'Taxa de Poupança Crítica',
        description: `Sua taxa de poupança está em ${health.savingsRate.toFixed(1)}%. Recomenda-se pelo menos 10% da renda.`,
        priority: 'high'
      });
    }

    // Alert para tendência de aumento de gastos
    if (patterns.spendingTrend === 'increasing') {
      alerts.push({
        type: 'warning',
        icon: FaArrowUp,
        title: 'Gastos em Crescimento',
        description: 'Seus gastos têm aumentado nos últimos meses. Monitore as categorias principais.',
        priority: 'medium'
      });
    }

    // Alert para transações incomuns
    if (patterns.unusualTransactions.length > 0) {
      alerts.push({
        type: 'info',
        icon: FaSearch,
        title: 'Transações Incomuns Detectadas',
        description: `${patterns.unusualTransactions.length} transações com valores bem acima da média foram identificadas.`,
        priority: 'low'
      });
    }

    return alerts.sort((a, b) => {
      const priorityOrder = { high: 3, medium: 2, low: 1 };
      return priorityOrder[b.priority] - priorityOrder[a.priority];
    });
  };

  const generateRecommendations = (filteredTransactions) => {
    const recommendations = [];
    const savings = findSavingsOpportunities(filteredTransactions);
    const health = analyzeBudgetHealth(filteredTransactions);

    // Recomendações baseadas na saúde financeira
    if (health.savingsRate < 10) {
      recommendations.push({
        category: 'Poupança',
        title: 'Aumente sua Taxa de Poupança',
        description: 'Tente economizar pelo menos 10% da sua renda mensal. Comece com pequenos cortes nas categorias de maior gasto.',
        impact: 'high',
        effort: 'medium'
      });
    }

    // Recomendações baseadas nas categorias de maior gasto
    if (savings.topExpenseCategories.length > 0) {
      const topCategory = savings.topExpenseCategories[0];
      recommendations.push({
        category: 'Otimização',
        title: `Revise gastos em ${topCategory.name}`,
        description: `Esta categoria representa ${formatCurrency(topCategory.total)} dos seus gastos. Pequenas reduções aqui podem ter grande impacto.`,
        impact: 'high',
        effort: 'low'
      });
    }

    // Recomendação para automação
    if (savings.frequentCategories.length > 0) {
      recommendations.push({
        category: 'Automação',
        title: 'Considere Automatizar Pagamentos',
        description: 'Algumas categorias têm transações muito frequentes. Automatizar pode ajudar a controlar melhor os gastos.',
        impact: 'medium',
        effort: 'low'
      });
    }

    return recommendations;
  };

  const getMonthName = (monthNumber) => {
    const monthNames = [
      'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ];
    return monthNames[monthNumber - 1];
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(amount);
  };

  const getHealthText = (level) => {
    switch (level) {
      case 'excellent': return 'Excelente';
      case 'good': return 'Boa';
      case 'fair': return 'Regular';
      case 'poor': return 'Ruim';
      case 'critical': return 'Crítica';
      default: return 'N/A';
    }
  };

  if (loading) {
    return (
      <Container>
        <div className="text-center mt-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h1 className="display-6 mb-3">
                <FaBrain className="me-3" />
                Insights Financeiros
              </h1>
              <p className="text-muted">Análises inteligentes e recomendações personalizadas para suas finanças</p>
            </div>
            <div>
              <Button as={Link} to="/reports" variant="outline-primary" className="d-flex align-items-center">
                <FaHome className="me-2" />
                Central de Relatórios
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {error && (
        <Row className="mb-4">
          <Col>
            <Alert variant="danger">{error}</Alert>
          </Col>
        </Row>
      )}

      {/* Filtro de Período */}
      <Row className="mb-4">
        <Col md={3}>
          <Card>
            <Card.Body>
              <Form.Group>
                <Form.Label><strong>Período de Análise</strong></Form.Label>
                <Form.Select
                  value={selectedTimeframe}
                  onChange={(e) => setSelectedTimeframe(e.target.value)}
                >
                  <option value="3months">Últimos 3 meses</option>
                  <option value="6months">Últimos 6 meses</option>
                  <option value="12months">Último ano</option>
                </Form.Select>
              </Form.Group>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {insights.budgetHealth && (
        <>
          {/* Saúde Financeira */}
          <Row className="mb-4">
            <Col>
              <Card>
                <Card.Header>
                  <h5 className="mb-0">Saúde Financeira Geral</h5>
                </Card.Header>
                <Card.Body>
                  <Row className="align-items-center">
                    <Col md={8}>
                      <div className="mb-3">
                        <h4>
                          <Badge bg={insights.budgetHealth.healthColor} className="me-2">
                            {getHealthText(insights.budgetHealth.healthLevel)}
                          </Badge>
                          Score: {insights.budgetHealth.healthScore}/100
                        </h4>
                        <ProgressBar 
                          variant={insights.budgetHealth.healthColor}
                          now={insights.budgetHealth.healthScore}
                          style={{ height: '10px' }}
                        />
                      </div>
                      <div className="row">
                        <div className="col-md-4">
                          <small className="text-muted">Receita Total</small>
                          <div className="fw-bold text-success">{formatCurrency(insights.budgetHealth.totalIncome)}</div>
                        </div>
                        <div className="col-md-4">
                          <small className="text-muted">Despesa Total</small>
                          <div className="fw-bold text-danger">{formatCurrency(insights.budgetHealth.totalExpense)}</div>
                        </div>
                        <div className="col-md-4">
                          <small className="text-muted">Taxa de Poupança</small>
                          <div className={`fw-bold ${insights.budgetHealth.savingsRate >= 10 ? 'text-success' : 'text-warning'}`}>
                            {insights.budgetHealth.savingsRate.toFixed(1)}%
                          </div>
                        </div>
                      </div>
                    </Col>
                    <Col md={4} className="text-center">
                      <div className="position-relative d-inline-block">
                        <div className="circular-progress" style={{
                          background: `conic-gradient(var(--bs-${insights.budgetHealth.healthColor}) ${insights.budgetHealth.healthScore * 3.6}deg, #e9ecef 0deg)`
                        }}>
                          <div className="circular-progress-inner">
                            <span className="h4 mb-0">{insights.budgetHealth.healthScore}</span>
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Alertas de Risco */}
          {insights.riskAlerts && insights.riskAlerts.length > 0 && (
            <Row className="mb-4">
              <Col>
                <Card>
                  <Card.Header>
                    <h5 className="mb-0">Alertas e Atenção</h5>
                  </Card.Header>
                  <Card.Body>
                    {insights.riskAlerts.map((alert, index) => {
                      const IconComponent = alert.icon;
                      return (
                        <Alert key={index} variant={alert.type} className="d-flex align-items-center">
                          <IconComponent className="me-3" size={20} />
                          <div>
                            <strong>{alert.title}</strong>
                            <div>{alert.description}</div>
                          </div>
                        </Alert>
                      );
                    })}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {/* Recomendações */}
          {insights.recommendations && insights.recommendations.length > 0 && (
            <Row className="mb-4">
              <Col>
                <Card>
                  <Card.Header>
                    <h5 className="mb-0"><FaLightbulb className="me-2" />Recomendações Personalizadas</h5>
                  </Card.Header>
                  <Card.Body>
                    <Accordion>
                      {insights.recommendations.map((rec, index) => (
                        <Accordion.Item key={index} eventKey={index.toString()}>
                          <Accordion.Header>
                            <div className="d-flex justify-content-between align-items-center w-100 me-3">
                              <span><strong>{rec.title}</strong></span>
                              <div>
                                <Badge bg="primary" className="me-2">{rec.category}</Badge>
                                <Badge bg={rec.impact === 'high' ? 'success' : rec.impact === 'medium' ? 'warning' : 'secondary'}>
                                  Impacto {rec.impact === 'high' ? 'Alto' : rec.impact === 'medium' ? 'Médio' : 'Baixo'}
                                </Badge>
                              </div>
                            </div>
                          </Accordion.Header>
                          <Accordion.Body>
                            {rec.description}
                          </Accordion.Body>
                        </Accordion.Item>
                      ))}
                    </Accordion>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {/* Análise de Padrões de Gastos */}
          {insights.spendingPatterns && (
            <Row className="mb-4">
              <Col md={6}>
                <Card className="h-100">
                  <Card.Header>
                    <h5 className="mb-0">Padrões de Gastos</h5>
                  </Card.Header>
                  <Card.Body>
                    <div className="mb-3">
                      <small className="text-muted">Tendência Recente</small>
                      <div className="d-flex align-items-center">
                        {insights.spendingPatterns.spendingTrend === 'increasing' ? (
                          <>
                            <FaArrowUp className="text-danger me-2" />
                            <span className="text-danger">Gastos aumentando</span>
                          </>
                        ) : (
                          <>
                            <FaArrowDown className="text-success me-2" />
                            <span className="text-success">Gastos diminuindo</span>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="mb-3">
                      <small className="text-muted">Média Mensal</small>
                      <div>
                        <span className="text-success">Receita: {formatCurrency(insights.spendingPatterns.avgMonthlyIncome)}</span>
                        <br />
                        <span className="text-danger">Despesa: {formatCurrency(insights.spendingPatterns.avgMonthlyExpense)}</span>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={6}>
                <Card className="h-100">
                  <Card.Header>
                    <h5 className="mb-0">Transações Incomuns</h5>
                  </Card.Header>
                  <Card.Body>
                    {insights.spendingPatterns.unusualTransactions.length > 0 ? (
                      <Table responsive size="sm">
                        <thead>
                          <tr>
                            <th>Data</th>
                            <th>Categoria</th>
                            <th>Valor</th>
                          </tr>
                        </thead>
                        <tbody>
                          {insights.spendingPatterns.unusualTransactions.map((transaction, index) => (
                            <tr key={index}>
                              <td>{new Date(transaction.date).toLocaleDateString('pt-BR')}</td>
                              <td>{transaction.categoryName}</td>
                              <td className="text-danger">{formatCurrency(Math.abs(transaction.amount))}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    ) : (
                      <div className="text-center text-muted">
                        <FaCheckCircle className="mb-2" size={24} />
                        <div>Nenhuma transação incomum detectada</div>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {/* Oportunidades de Economia */}
          {insights.savingsOpportunities && (
            <Row className="mb-4">
              <Col>
                <Card>
                  <Card.Header>
                    <h5 className="mb-0">Oportunidades de Economia</h5>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <h6>Top Categorias por Gasto</h6>
                        <Table responsive size="sm">
                          <thead>
                            <tr>
                              <th>Categoria</th>
                              <th className="text-end">Total</th>
                              <th className="text-center">Transações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {insights.savingsOpportunities.topExpenseCategories.map((category, index) => (
                              <tr key={index}>
                                <td>{category.name}</td>
                                <td className="text-end">{formatCurrency(category.total)}</td>
                                <td className="text-center">{category.count}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Col>
                      <Col md={6}>
                        <h6>Categorias Mais Frequentes</h6>
                        <Table responsive size="sm">
                          <thead>
                            <tr>
                              <th>Categoria</th>
                              <th className="text-center">Frequência</th>
                              <th className="text-end">Média</th>
                            </tr>
                          </thead>
                          <tbody>
                            {insights.savingsOpportunities.frequentCategories.map((category, index) => (
                              <tr key={index}>
                                <td>{category.name}</td>
                                <td className="text-center">{category.count}</td>
                                <td className="text-end">{formatCurrency(category.avg)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </>
      )}
    </Container>
  );
};

export default FinancialInsights; 